import {
  CallbackListener,
  Player,
  PlayerRef,
  RenderPoster,
} from '@remotion/player';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AbsoluteFill } from 'remotion';
import { LoadingSpinner } from '@crabs-ai/ds-react';
import { MIN_TIMELINE_DURATION_IN_SECONDS } from '../utils/get-visible-frames';
import { MainComposition, MainCompositionProps } from './composition';

export const RemotionPlayer = ({
  playerRef,
  loop,
  compositionHeight,
  compositionWidth,
  durationInFrames,
  fps,
  enableMaskEditing = false,
  autoPlay = false,
}: {
  playerRef: React.RefObject<PlayerRef | null>;
  loop: boolean;
  compositionWidth: number;
  compositionHeight: number;
  durationInFrames: number;
  fps: number;
  enableMaskEditing?: boolean;
  autoPlay?: boolean;
}) => {
  const [shouldShowPlayerControls, setShouldShowPlayerControls] =
    useState(false);

  useEffect(() => {
    const current = playerRef.current;

    if (!current) {
      return;
    }

    const handleFullscreenChange: CallbackListener<'fullscreenchange'> = (
      event,
    ) => {
      setShouldShowPlayerControls(event.detail.isFullscreen);
    };

    current.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      current.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [playerRef]);

  const inputProps: MainCompositionProps = useMemo(() => {
    return {
      playerRef,
      enableMaskEditing,
      shouldShowPlanWatermark: false,
      showWatermarkInPlayer: false,
    };
  }, [enableMaskEditing, playerRef]);

  const style = useMemo(() => {
    return {
      width: '100%',
      overflow: 'visible',
      height: '100%',
    };
  }, []);

  const browserMediaControlsBehavior = useMemo(() => {
    return { mode: 'register-media-session' } as const;
  }, []);

  const durationInFramesToUse = useMemo(() => {
    // item이 없어도 timeline 스크럽을 허용
    if (durationInFrames === 0) {
      return MIN_TIMELINE_DURATION_IN_SECONDS * fps;
    }

    return durationInFrames;
  }, [durationInFrames, fps]);

  const renderPoster: RenderPoster = useCallback(({ isBuffering }) => {
    if (isBuffering) {
      return (
        <AbsoluteFill
          style={{
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          }}
        >
          <div
            role="status"
            aria-label="Buffering"
            className="h-[120px] w-[120px] [&>svg]:h-full [&>svg]:w-full"
          >
            <LoadingSpinner
              size="large"
              className="fill-white"
            />
          </div>
        </AbsoluteFill>
      );
    }

    return null;
  }, []);

  return (
    <Player
      acknowledgeRemotionLicense
      ref={playerRef}
      component={MainComposition}
      durationInFrames={durationInFramesToUse}
      compositionHeight={compositionHeight}
      compositionWidth={compositionWidth}
      controls={shouldShowPlayerControls}
      fps={fps}
      style={style}
      inputProps={inputProps}
      loop={loop}
      autoPlay={autoPlay}
      overflowVisible
      moveToBeginningWhenEnded={autoPlay}
      browserMediaControlsBehavior={browserMediaControlsBehavior}
      numberOfSharedAudioTags={12}
      renderPoster={renderPoster}
      showPosterWhenBuffering
    />
  );
};
